import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { querySuiteRuns } from '@/features/executions/data';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks/useQualityDateRange';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useDateWindow } from '@/features/shared/hooks/useDateWindow';
import {
  collapseStatusOptions,
  expandStatusFilterValues,
} from '@/features/shared/utils/projectStatusPresentation';
import { querySuites } from '@/features/suites/data/suites';
import { useSuiteOptions } from '@/features/suites/hooks/useSuiteOptions';
import {
  suiteRunDefaultColumnVisibilityModel,
  suiteRunDefaultSortDirection,
  suiteRunDefaultSortKey,
  suiteRunSortFieldMap,
  suiteRunStatusOptions,
  type SuiteRunSortDirection,
  type SuiteRunSortKey,
} from '@/features/suites/pages/suiteRunsTableDefinition';
import { SuitesPageHistorySection } from '@/features/suites/pages/SuitesPageHistorySection';
import { SuitesPageStatusSection } from '@/features/suites/pages/SuitesPageStatusSection';
import { SuitesPageTabbedPanel } from '@/features/suites/pages/SuitesPageTabbedPanel';
import {
  suiteDefaultColumnVisibilityModel,
  suiteDefaultSortDirection,
  suiteDefaultSortKey,
  suiteSortFieldMap,
  type SuiteSortDirection,
  type SuiteSortKey,
} from '@/features/suites/pages/suitesTableDefinition';
import { useSuitesPageFilterOptionCounts } from '@/features/suites/pages/useSuitesPageFilterOptionCounts';
import { useSuitesPageStatusMetricCards } from '@/features/suites/pages/useSuitesPageStatusMetricCards';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Box, Text, type GridColumnVisibilityModel } from '@/ui/primitives';
import type { ExecutionStatusKey } from '@/features/metrics/metricsHelpers';
import type { MetricsStatusChartPanelModelToolbarState } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { EntityTrendsPageViewModel } from '@/features/shared/hooks/useEntityTrendsPage';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteRunDto, SuiteRunStatus, SuiteListItemDto } from '@flowqa/shared';

type TabbedSuitesPanelTab = 'suites' | 'executions';

const isSuiteSortKey = (field: string | undefined): field is SuiteSortKey =>
  field !== undefined && Object.prototype.hasOwnProperty.call(suiteSortFieldMap, field);

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const toMetricsCountMap = (
  statusSummary: Array<{ key: ExecutionStatusKey; value: number }> | undefined,
): Partial<Record<ExecutionStatusKey, number>> =>
  Object.fromEntries((statusSummary ?? []).map((entry) => [entry.key, entry.value])) as Partial<
    Record<ExecutionStatusKey, number>
  >;

const sumMetricsCounts = (counts: Partial<Record<ExecutionStatusKey, number>>): number =>
  Object.values(counts).reduce((sum, value) => sum + value, 0);

type SuitesPageContentProps = {
  viewModel: EntityTrendsPageViewModel<SuiteListItemDto>;
};

export const SuitesPageContent = ({ viewModel }: SuitesPageContentProps): ReactElement => {
  const [searchParams] = useSearchParams();
  const initialPanelColumnVisibilityModel: GridColumnVisibilityModel = {
    ...suiteDefaultColumnVisibilityModel,
  };
  const initialPanelExecutionColumnVisibilityModel: GridColumnVisibilityModel = {
    ...suiteRunDefaultColumnVisibilityModel,
  };
  const [panelDateQuickFilter, setPanelDateQuickFilter] = useState<DateQuickFilter>('7d');
  const [panelFromDate, setPanelFromDate] = useState<Dayjs | null>(null);
  const [panelToDate, setPanelToDate] = useState<Dayjs | null>(null);
  const [debouncedPanelSearch, setDebouncedPanelSearch] = useState('');
  const [panelColumnVisibilityModel, setPanelColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialPanelColumnVisibilityModel);
  const [panelSortKey, setPanelSortKey] = useState<SuiteSortKey>(suiteDefaultSortKey);
  const [panelSortDirection, setPanelSortDirection] =
    useState<SuiteSortDirection>(suiteDefaultSortDirection);
  const [panelFilterStatuses, setPanelFilterStatuses] = useState<SuiteRunStatus[]>([]);
  const [panelFilterSuites, setPanelFilterSuites] = useState<string[]>([]);
  const [panelFilterTags, setPanelFilterTags] = useState<string[]>([]);
  const [panelActiveTab, setPanelActiveTab] = useState<TabbedSuitesPanelTab>('suites');
  const [panelExecutionColumnVisibilityModel, setPanelExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialPanelExecutionColumnVisibilityModel);
  const [panelExecutionSortKey, setPanelExecutionSortKey] =
    useState<SuiteRunSortKey>(suiteRunDefaultSortKey);
  const [panelExecutionSortDirection, setPanelExecutionSortDirection] =
    useState<SuiteRunSortDirection>(suiteRunDefaultSortDirection);
  const [panelSuitesMeta, setPanelSuitesMeta] = useState<TableQueryMeta<SuiteListItemDto> | null>(
    null,
  );
  const [panelSuiteRunsMeta, setPanelSuiteRunsMeta] = useState<TableQueryMeta<SuiteRunDto> | null>(
    null,
  );
  const { suiteOptions } = useSuiteOptions({
    projectId: viewModel.selectedProjectId,
    enabled: panelSuitesMeta !== null || panelSuiteRunsMeta !== null,
  });
  const panelAvailableTags = useMemo(
    () =>
      Array.from(new Set(viewModel.tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [viewModel.tagOptions],
  );
  const panelAvailableTagOptions = useMemo(
    () => panelAvailableTags.map((tag) => ({ value: tag, label: tag })),
    [panelAvailableTags],
  );
  const enabledDebugSections = useMemo(() => {
    const rawValue = searchParams.get('debugSuitesSections');
    if (!rawValue) return null;
    return new Set(
      rawValue
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.length > 0),
    );
  }, [searchParams]);
  const showStatusSection = enabledDebugSections === null || enabledDebugSections.has('status');
  const showHistorySection = enabledDebugSections === null || enabledDebugSections.has('history');
  const showPanelSection = enabledDebugSections === null || enabledDebugSections.has('clone');
  const debugRenderMode = searchParams.get('debugSuitesRender');
  const handleDebouncedPanelSearchChange = useCallback((value: string) => {
    setDebouncedPanelSearch(value);
  }, []);
  const showPanelDeltas = viewModel.deltasEnabled;
  const effectivePanelFilterStatuses = useMemo(
    () => expandStatusFilterValues(panelFilterStatuses, viewModel.groupErroredWithFailed),
    [panelFilterStatuses, viewModel.groupErroredWithFailed],
  );
  const panelStatusOptions = useMemo(
    () => collapseStatusOptions(suiteRunStatusOptions, viewModel.groupErroredWithFailed),
    [viewModel.groupErroredWithFailed],
  );
  const panelExecutionStatusOptions = useMemo(
    () => collapseStatusOptions(suiteRunStatusOptions, viewModel.groupErroredWithFailed),
    [viewModel.groupErroredWithFailed],
  );
  const panelDateWindow = useDateWindow({
    dateQuickFilter: panelDateQuickFilter,
    fromDate: panelFromDate,
    toDate: panelToDate,
  });
  const { countedSuiteOptions, countedStatusOptions, countedTagOptions } =
    useSuitesPageFilterOptionCounts({
      enabled: panelSuitesMeta !== null || panelSuiteRunsMeta !== null,
      selectedProjectId: viewModel.selectedProjectId,
      selectedOrganisationId: viewModel.selectedOrganisationId ?? null,
      search: debouncedPanelSearch,
      selectedStatuses: effectivePanelFilterStatuses,
      selectedSuites: panelFilterSuites,
      selectedTags: panelFilterTags,
      startDay: panelDateWindow.startDay,
      endDay: panelDateWindow.endDay,
      suiteOptions,
      statusOptions: panelStatusOptions,
      tagOptions: panelAvailableTagOptions,
    });

  useEffect(() => {
    if (!viewModel.groupErroredWithFailed) {
      return;
    }
    setPanelFilterStatuses((current) => {
      if (!current.includes('errored')) {
        return current;
      }
      const next = Array.from(
        new Set(current.map((status) => (status === 'errored' ? 'failed' : status))),
      );
      return areStringArraysEqual(current, next) ? current : next;
    });
  }, [viewModel.groupErroredWithFailed]);

  const panelQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> => {
      const toolbarFilters = mergeFilters(
        buildInFilter('executionStatus', [...effectivePanelFilterStatuses]),
        buildInFilter('id', [...panelFilterSuites]),
        buildInFilter('tags', [...panelFilterTags]),
      );
      const requestedSort = input.sorts.length > 0 ? (input.sorts[0] ?? null) : null;
      const requestedSortKey = isSuiteSortKey(requestedSort?.field)
        ? requestedSort.field
        : panelSortKey;
      const sortOverride: ApiSort = {
        field: suiteSortFieldMap[requestedSortKey],
        direction: requestedSort?.direction ?? panelSortDirection,
      };
      return querySuites(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...input.filters, ...toolbarFilters],
          metricsStartDay: panelDateWindow.startDay ?? undefined,
          metricsEndDay: panelDateWindow.endDay ?? undefined,
          previousMetricsStartDay: panelDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: panelDateWindow.previousEndDay ?? undefined,
          include: {
            latestRun: true,
            executionStatusCounts: true,
            executionSummaries: true,
            latestTestResults: { counts: true, summaries: true },
            metrics: true,
          },
        },
      );
    },
    [
      panelDateWindow.endDay,
      panelDateWindow.previousEndDay,
      panelDateWindow.previousStartDay,
      panelDateWindow.startDay,
      effectivePanelFilterStatuses,
      panelFilterSuites,
      panelFilterTags,
      panelSortDirection,
      panelSortKey,
      showPanelDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const panelExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteRunDto>> => {
      const isLatestQuickFilter =
        panelDateQuickFilter === 'latest' && panelFromDate === null && panelToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', [...effectivePanelFilterStatuses]),
        buildInFilter('suiteId', [...panelFilterSuites]),
        buildInFilter('tags', [...panelFilterTags]),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              panelDateWindow.startDay
                ? dayjs(panelDateWindow.startDay).startOf('day').toISOString()
                : null,
              panelDateWindow.endDay
                ? dayjs(panelDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: suiteRunSortFieldMap[panelExecutionSortKey],
        direction: panelExecutionSortDirection,
      };
      return querySuiteRuns(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedPanelSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      panelDateWindow.endDay,
      panelDateWindow.startDay,
      panelDateQuickFilter,
      effectivePanelFilterStatuses,
      panelExecutionSortDirection,
      panelExecutionSortKey,
      panelFromDate,
      panelToDate,
      panelFilterSuites,
      panelFilterTags,
      debouncedPanelSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const suitesEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'suite' });
  const suiteRunsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'suiteRun' });
  const suitesRangeEmptyState = getEmptyStateMessage('entity.noneForSelectedRange', {
    entity: 'suite',
  });
  const suiteRunsRangeEmptyState = getEmptyStateMessage('entity.noneForSelectedRange', {
    entity: 'suiteRun',
  });
  const panelNoRowsText = panelDateWindow.hasCustomBounds
    ? suitesRangeEmptyState
    : formatRangeWindowNoDataText(suitesEmptyState, panelDateQuickFilter, false);
  const panelExecutionsNoRowsText = panelDateWindow.hasCustomBounds
    ? suiteRunsRangeEmptyState
    : formatRangeWindowNoDataText(suiteRunsEmptyState, panelDateQuickFilter, false);
  const panelChartNoRowsText = panelExecutionsNoRowsText;
  const panelStatusFilterKey = useMemo(
    () =>
      [...effectivePanelFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [effectivePanelFilterStatuses],
  );
  const hasCustomMetricDateRange = panelFromDate !== null || panelToDate !== null;
  const isLatestOnlyMetricMode =
    panelDateQuickFilter === 'latest' && panelFromDate === null && panelToDate === null;
  const showMetricDelta =
    panelDateQuickFilter !== 'latest' &&
    panelDateWindow.previousStartDay !== null &&
    panelDateWindow.previousEndDay !== null;
  const totalSecondaryValue = viewModel.metricCards.find(
    (card) => card.id === 'total',
  )?.secondaryValue;
  const panelStatusMetricCards = useSuitesPageStatusMetricCards({
    debouncedPanelSearch,
    effectivePanelFilterStatuses,
    groupErroredWithFailed: viewModel.groupErroredWithFailed,
    isLatestOnlyMetricMode,
    panelDateWindow,
    panelFilterSuites,
    panelFilterTags,
    panelStatusOptions,
    selectedOrganisationId: viewModel.selectedOrganisationId,
    selectedProjectId: viewModel.selectedProjectId,
    showMetricDelta,
    totalSecondaryValue,
  });
  const selectedSuiteMetricEntityId = useMemo(
    () => (panelFilterSuites.length === 1 ? (panelFilterSuites[0] ?? null) : null),
    [panelFilterSuites],
  );
  const suiteMetricsQuery = useMetricsQuery({
    range: hasCustomMetricDateRange || isLatestOnlyMetricMode ? 'all' : panelDateQuickFilter,
    statuses: effectivePanelFilterStatuses as ExecutionStatusKey[],
    tags: panelFilterTags,
    scope: 'suite',
    projectId: viewModel.selectedProjectId,
    entityId: selectedSuiteMetricEntityId,
    name: debouncedPanelSearch.trim().length > 0 ? debouncedPanelSearch : null,
    startDay: hasCustomMetricDateRange ? (panelFromDate?.format('YYYY-MM-DD') ?? null) : null,
    endDay: hasCustomMetricDateRange ? (panelToDate?.format('YYYY-MM-DD') ?? null) : null,
    latestOnly: isLatestOnlyMetricMode,
    enabled: selectedSuiteMetricEntityId !== null,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const previousSuiteMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: effectivePanelFilterStatuses as ExecutionStatusKey[],
    tags: panelFilterTags,
    scope: 'suite',
    projectId: viewModel.selectedProjectId,
    entityId: selectedSuiteMetricEntityId,
    name: debouncedPanelSearch.trim().length > 0 ? debouncedPanelSearch : null,
    startDay: showMetricDelta ? panelDateWindow.previousStartDay : null,
    endDay: showMetricDelta ? panelDateWindow.previousEndDay : null,
    enabled: selectedSuiteMetricEntityId !== null && showMetricDelta,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const suiteMetricCards = useMemo(() => {
    if (selectedSuiteMetricEntityId === null) {
      return panelStatusMetricCards;
    }
    const counts = toMetricsCountMap(suiteMetricsQuery.data?.statusSummary);
    const previousCounts = toMetricsCountMap(previousSuiteMetricsQuery.data?.statusSummary);
    return buildStatusMetricCards({
      options: mapStatusOptionsToMetricOptions(panelStatusOptions, resolveMetricTone),
      counts,
      deltas: showMetricDelta ? buildStatusDeltaMap(counts, previousCounts) : undefined,
      total: sumMetricsCounts(counts),
      previousCounts,
      previousTotal: showMetricDelta ? sumMetricsCounts(previousCounts) : undefined,
      loading:
        suiteMetricsQuery.data === undefined &&
        suiteMetricsQuery.isFetching &&
        !suiteMetricsQuery.isError,
      showDelta: showMetricDelta,
      totalLabel: 'Suite Runs',
      totalSecondaryValue,
    });
  }, [
    previousSuiteMetricsQuery.data?.statusSummary,
    selectedSuiteMetricEntityId,
    showMetricDelta,
    suiteMetricsQuery.data,
    suiteMetricsQuery.isError,
    suiteMetricsQuery.isFetching,
    panelStatusOptions,
    panelStatusMetricCards,
    totalSecondaryValue,
  ]);
  const panelTagFilterKey = useMemo(
    () => [...panelFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterTags],
  );
  const panelSuiteFilterKey = useMemo(
    () => [...panelFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterSuites],
  );
  const panelLatestBoundsFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('executionStatus', [...effectivePanelFilterStatuses]),
        buildInFilter('id', [...panelFilterSuites]),
        buildInFilter('tags', [...panelFilterTags]),
      ),
    [effectivePanelFilterStatuses, panelFilterSuites, panelFilterTags],
  );
  const panelLatestBoundsQueryEnabled =
    panelDateQuickFilter === 'latest' && panelFromDate === null && panelToDate === null;
  const earliestLatestSuiteQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suites,
      'toolbar-latest-bounds',
      JSON.stringify({
        panelSearchValue: debouncedPanelSearch,
        effectivePanelFilterStatuses,
        panelFilterSuites,
        panelFilterTags,
        selectedOrganisationId: viewModel.selectedOrganisationId,
        selectedProjectId: viewModel.selectedProjectId,
        direction: 'asc',
      }),
    ),
    queryFn: async () =>
      await querySuites(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          search: debouncedPanelSearch.trim(),
          sorts: [{ field: 'latestRunTimestamp', direction: 'asc' }],
          filters: panelLatestBoundsFilters,
          include: { latestRun: true },
        },
      ),
    enabled: panelLatestBoundsQueryEnabled,
  });
  const latestLatestSuiteQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suites,
      'toolbar-latest-bounds',
      JSON.stringify({
        panelSearchValue: debouncedPanelSearch,
        effectivePanelFilterStatuses,
        panelFilterSuites,
        panelFilterTags,
        selectedOrganisationId: viewModel.selectedOrganisationId,
        selectedProjectId: viewModel.selectedProjectId,
        direction: 'desc',
      }),
    ),
    queryFn: async () =>
      await querySuites(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          search: debouncedPanelSearch.trim(),
          sorts: [{ field: 'latestRunTimestamp', direction: 'desc' }],
          filters: panelLatestBoundsFilters,
          include: { latestRun: true },
        },
      ),
    enabled: panelLatestBoundsQueryEnabled,
  });
  const panelSuiteLatestDateBounds = useMemo(
    () => resolveMetaDateBounds(panelSuitesMeta, (item) => item.latestRun?.timestamp ?? null),
    [panelSuitesMeta],
  );
  const panelSuiteRunDateBounds = useMemo(
    () => resolveMetaDateBounds(panelSuiteRunsMeta, (item) => item.timestamp),
    [panelSuiteRunsMeta],
  );
  const fullLatestDateBounds = useMemo(() => {
    const earliestLatestTimestamp =
      earliestLatestSuiteQuery.data?.items[0]?.latestRun?.timestamp ?? null;
    const latestLatestTimestamp =
      latestLatestSuiteQuery.data?.items[0]?.latestRun?.timestamp ?? null;
    if (earliestLatestTimestamp !== null || latestLatestTimestamp !== null) {
      return {
        from: earliestLatestTimestamp ? dayjs(earliestLatestTimestamp) : null,
        to: latestLatestTimestamp ? dayjs(latestLatestTimestamp) : null,
      };
    }
    return {
      from: null,
      to: null,
    };
  }, [earliestLatestSuiteQuery.data?.items, latestLatestSuiteQuery.data?.items]);
  const historyLatestDateBounds = useMemo(() => {
    if (fullLatestDateBounds.from !== null || fullLatestDateBounds.to !== null) {
      return fullLatestDateBounds;
    }
    return panelSuiteRunDateBounds.from !== null || panelSuiteRunDateBounds.to !== null
      ? panelSuiteRunDateBounds
      : panelSuiteLatestDateBounds;
  }, [panelSuiteLatestDateBounds, panelSuiteRunDateBounds, fullLatestDateBounds]);
  const sharedChartToolbarState = useMemo<MetricsStatusChartPanelModelToolbarState<SuiteRunStatus>>(
    () => ({
      search: debouncedPanelSearch,
      setSearch: (value) => {
        handleDebouncedPanelSearchChange(value);
        viewModel.handleSearchChange(value);
      },
      selectedStatuses: panelFilterStatuses,
      setSelectedStatuses: (next) => {
        setPanelFilterStatuses(next);
        viewModel.handleStatusesChange(next);
      },
      selectedEntities: panelFilterSuites,
      setSelectedEntities: setPanelFilterSuites,
      entityOptions: countedSuiteOptions,
      entityLabel: 'Suite',
      selectedProjects: viewModel.selectedProjects,
      setSelectedProjects: viewModel.handleProjectsChange,
      projectOptions: viewModel.showProjectFilter ? viewModel.projectFilterOptions : undefined,
      selectedTags: panelFilterTags,
      setSelectedTags: (next) => {
        setPanelFilterTags(next);
        viewModel.handleTagsChange(next);
      },
      noRowsText: panelChartNoRowsText,
      latestQueryStartDay:
        historyLatestDateBounds.from?.format('YYYY-MM-DD') ?? viewModel.pickerStartDay,
      latestQueryEndDay: historyLatestDateBounds.to?.format('YYYY-MM-DD') ?? viewModel.pickerEndDay,
      dateQuickFilter: panelDateQuickFilter,
      onDateQuickFilterChange: (next) => {
        setPanelDateQuickFilter(next);
        setPanelFromDate(null);
        setPanelToDate(null);
        viewModel.handleRangeChange(next);
        viewModel.handleSelectedStartDayChange(null);
        viewModel.handleSelectedEndDayChange(null);
      },
      fromDate: panelFromDate,
      toDate: panelToDate,
      onFromDateChange: (value) => {
        const nextValue = value ? dayjs(value) : null;
        setPanelFromDate(nextValue);
        viewModel.handleSelectedStartDayChange(nextValue ? nextValue.format('YYYY-MM-DD') : null);
      },
      onToDateChange: (value) => {
        const nextValue = value ? dayjs(value) : null;
        setPanelToDate(nextValue);
        viewModel.handleSelectedEndDayChange(nextValue ? nextValue.format('YYYY-MM-DD') : null);
      },
    }),
    [
      panelDateQuickFilter,
      panelFilterStatuses,
      panelFilterSuites,
      panelFilterTags,
      panelFromDate,
      panelChartNoRowsText,
      panelToDate,
      debouncedPanelSearch,
      handleDebouncedPanelSearchChange,
      historyLatestDateBounds.from,
      historyLatestDateBounds.to,
      countedSuiteOptions,
      countedStatusOptions,
      countedTagOptions,
      viewModel.handleRangeChange,
      viewModel.handleSearchChange,
      viewModel.handleSelectedEndDayChange,
      viewModel.handleSelectedStartDayChange,
      viewModel.handleStatusesChange,
      viewModel.handleTagsChange,
      viewModel.pickerEndDay,
      viewModel.pickerStartDay,
      viewModel.projectFilterOptions,
      viewModel.selectedProjects,
      viewModel.showProjectFilter,
    ],
  );
  if (debugRenderMode === 'minimal') {
    return (
      <Box sx={{ minHeight: '8rem' }}>
        <Text role="body">Suites debug minimal render</Text>
      </Box>
    );
  }
  return (
    <Stack gap="2xl">
      {showStatusSection ? (
        <SuitesPageStatusSection
          availableTags={countedTagOptions}
          filterStatuses={panelFilterStatuses}
          groupErroredWithFailed={viewModel.groupErroredWithFailed}
          loading={viewModel.showMetricCardsSkeleton}
          metricCards={suiteMetricCards}
          projectId={viewModel.selectedProjectId}
          sharedChartToolbarState={sharedChartToolbarState}
          statusOptions={countedStatusOptions}
          suppressAnimation={viewModel.suppressChartAnimation}
          showPoints={viewModel.showLineChartPoints}
          onStatusToggle={(status) => {
            if (status === 'total') return;
            setPanelFilterStatuses((prev) =>
              prev.includes(status as SuiteRunStatus)
                ? prev.filter((item) => item !== status)
                : [...prev, status as SuiteRunStatus],
            );
          }}
        />
      ) : null}
      {showHistorySection ? (
        <Stack>
          <SuitesPageHistorySection
            panelDateQuickFilter={panelDateQuickFilter}
            panelDateWindow={panelDateWindow}
            panelFilterStatuses={effectivePanelFilterStatuses}
            panelFilterSuites={panelFilterSuites}
            panelFilterTags={panelFilterTags}
            panelNoRowsText={panelNoRowsText}
            panelSearchValue={debouncedPanelSearch}
            latestDateBounds={historyLatestDateBounds}
            selectedOrganisationId={viewModel.selectedOrganisationId}
            selectedProjectId={viewModel.selectedProjectId}
          />
        </Stack>
      ) : null}
      {showPanelSection ? (
        <Stack>
          <SuitesPageTabbedPanel
            panelActiveTab={panelActiveTab}
            setPanelActiveTab={setPanelActiveTab}
            panelDateQuickFilter={panelDateQuickFilter}
            setPanelDateQuickFilter={setPanelDateQuickFilter}
            panelFromDate={panelFromDate}
            setPanelFromDate={setPanelFromDate}
            panelToDate={panelToDate}
            setPanelToDate={setPanelToDate}
            panelColumnVisibilityModel={panelColumnVisibilityModel}
            setPanelColumnVisibilityModel={setPanelColumnVisibilityModel}
            panelSortKey={panelSortKey}
            setPanelSortKey={setPanelSortKey}
            panelSortDirection={panelSortDirection}
            setPanelSortDirection={setPanelSortDirection}
            panelFilterStatuses={panelFilterStatuses}
            setPanelFilterStatuses={setPanelFilterStatuses}
            panelFilterSuites={panelFilterSuites}
            setPanelFilterSuites={setPanelFilterSuites}
            panelFilterTags={panelFilterTags}
            setPanelFilterTags={setPanelFilterTags}
            panelExecutionColumnVisibilityModel={panelExecutionColumnVisibilityModel}
            setPanelExecutionColumnVisibilityModel={setPanelExecutionColumnVisibilityModel}
            panelExecutionSortKey={panelExecutionSortKey}
            setPanelExecutionSortKey={setPanelExecutionSortKey}
            panelExecutionSortDirection={panelExecutionSortDirection}
            setPanelExecutionSortDirection={setPanelExecutionSortDirection}
            panelAvailableTags={panelAvailableTags}
            suiteOptions={countedSuiteOptions}
            panelDateWindow={panelDateWindow}
            panelStatusFilterKey={panelStatusFilterKey}
            panelSuiteFilterKey={panelSuiteFilterKey}
            panelTagFilterKey={panelTagFilterKey}
            panelNoRowsText={panelNoRowsText}
            panelExecutionsNoRowsText={panelExecutionsNoRowsText}
            debouncedPanelSearch={debouncedPanelSearch}
            panelSuitesMeta={panelSuitesMeta}
            setPanelSuitesMeta={setPanelSuitesMeta}
            panelSuiteRunsMeta={panelSuiteRunsMeta}
            setPanelSuiteRunsMeta={setPanelSuiteRunsMeta}
            panelSuiteRunDateBounds={panelSuiteRunDateBounds}
            showPanelDeltas={showPanelDeltas}
            selectedOrganisationId={viewModel.selectedOrganisationId}
            selectedProjectId={viewModel.selectedProjectId}
            handleDebouncedPanelSearchChange={handleDebouncedPanelSearchChange}
            panelQueryFn={panelQueryFn}
            panelExecutionQueryFn={panelExecutionQueryFn}
            tagOptionsOverride={viewModel.tagOptions}
            statusOptionsOverride={panelStatusOptions}
            executionStatusOptionsOverride={panelExecutionStatusOptions}
          />
        </Stack>
      ) : null}
    </Stack>
  );
};
